import {Injectable} from '@angular/core';
import ComparisonOperatorInterface from '@cdk/components/criteria/contracts/comparison-operator.interface';
import {CriteriaFieldProviderInterface} from '@cdk/components/criteria/contracts/criteria-field-provider.interface';
import CriteriaFieldValueInterface from '@cdk/components/criteria/contracts/criteria-field-value.interface';
import {
    CriteriaLoadDataResultInterface,
    emptyCriteriaLoadDataResult
} from '@cdk/components/criteria/contracts/criteria-load-data-result.interface';
import CriteriaFieldModel from '@cdk/components/criteria/model/criteria-field.model';
import Between from '@cdk/components/criteria/operators/between';
import Contains from '@cdk/components/criteria/operators/contains';
import EndsWith from '@cdk/components/criteria/operators/ends-with';
import Equal from '@cdk/components/criteria/operators/equal';
import GreaterThan from '@cdk/components/criteria/operators/greater-than';
import GreaterThanEqual from '@cdk/components/criteria/operators/greater-than-equal';
import IsFalse from '@cdk/components/criteria/operators/is-false';
import IsNotNull from '@cdk/components/criteria/operators/is-not-null';
import IsNull from '@cdk/components/criteria/operators/is-null';
import IsTrue from '@cdk/components/criteria/operators/is-true';
import LessThan from '@cdk/components/criteria/operators/less-than';
import LessThanEqual from '@cdk/components/criteria/operators/less-than-equal';
import NotContains from '@cdk/components/criteria/operators/not-contains';
import NotEndsWith from '@cdk/components/criteria/operators/not-ends-with';
import NotEqual from '@cdk/components/criteria/operators/not-equal';
import NotStartsWith from '@cdk/components/criteria/operators/not-starts-with';
import StartsWith from '@cdk/components/criteria/operators/starts-with';
import {SiglaMomentoDisparoRegraEtiqueta} from '@cdk/components/regra-etiqueta/enums/sigla-momento-disparo-regra-etiqueta.enum';
import {
    Classificacao,
    EspecieProcesso,
    Localizador,
    ModalidadeMeio,
    ModalidadeOrgaoCentral,
    MomentoDisparoRegraEtiqueta,
    Pagination,
    Setor,
} from '@cdk/models';
import {ClassificacaoService} from '@cdk/services/classificacao.service';
import {EspecieProcessoService} from '@cdk/services/especie-processo.service';
import {LocalizadorService} from '@cdk/services/localizador.service';
import {ModalidadeMeioService} from '@cdk/services/modalidade-meio.service';
import {ModalidadeOrgaoCentralService} from '@cdk/services/modalidade-orgao-central.service';
import {SetorService} from '@cdk/services/setor.service';
import {from, of} from 'rxjs';
import {map} from 'rxjs/operators';

@Injectable()
export class CriacaoProcessoFieldsProvider implements CriteriaFieldProviderInterface {

    constructor(
        private _setorService: SetorService,
        private _especieProcessoService: EspecieProcessoService,
        private _classificacaoService: ClassificacaoService,
        private _modalidadeMeioService: ModalidadeMeioService,
        private _modalidadeOrgaoCentralService: ModalidadeOrgaoCentralService,
        private _localizadorService: LocalizadorService,
    ) {
    }

    supports(context: any): boolean {
        return context instanceof MomentoDisparoRegraEtiqueta
            && context.sigla === SiglaMomentoDisparoRegraEtiqueta.PROCESSO_CRIACAO_PROCESSO_ADMINISTRATIVO;
    }

    /**
     * Retorna os campos para o momento disparo.
     */
    getFields(context: any): CriteriaFieldModel[]|null {
        return [
            new CriteriaFieldModel(
                'documentoAvulsoOrigem',
                'Processo com Ofício de Origem',
                [
                    new IsNull('não'),
                    new IsNotNull('sim')
                ],
                (operator: ComparisonOperatorInterface) => of(null),
                (operator?: ComparisonOperatorInterface) => null,
                (criteriaFieldValue: CriteriaFieldValueInterface) => of<CriteriaLoadDataResultInterface>({
                    data: criteriaFieldValue.valor,
                    getValor: () => criteriaFieldValue.valor,
                    getExibicao: () => ''
                }),
                10
            ),
            new CriteriaFieldModel(
                'semValorEconomico',
                'Processo Sem Valor Econômico',
                [
                    new IsTrue(),
                    new IsFalse()
                ],
                (operator: ComparisonOperatorInterface) => of(null),
                (operator?: ComparisonOperatorInterface) => null,
                (criteriaFieldValue: CriteriaFieldValueInterface) => of<CriteriaLoadDataResultInterface>({
                    data: criteriaFieldValue.valor,
                    getValor: () => criteriaFieldValue.valor,
                    getExibicao: () => ''
                }),
                20
            ),
            new CriteriaFieldModel(
                'protocoloEletronico',
                'Processo com Protocolo Eletrônico',
                [
                    new IsTrue(),
                    new IsFalse()
                ],
                (operator: ComparisonOperatorInterface) => of(null),
                (operator?: ComparisonOperatorInterface) => null,
                (criteriaFieldValue: CriteriaFieldValueInterface) => of<CriteriaLoadDataResultInterface>({
                    data: criteriaFieldValue.valor,
                    getValor: () => criteriaFieldValue.valor,
                    getExibicao: () => ''
                }),
                30
            ),
            new CriteriaFieldModel(
                'visibilidadeExterna',
                'Processo com Visibilidade Externa',
                [
                    new IsTrue(),
                    new IsFalse()
                ],
                (operator: ComparisonOperatorInterface) => of(null),
                (operator?: ComparisonOperatorInterface) => null,
                (criteriaFieldValue: CriteriaFieldValueInterface) => of<CriteriaLoadDataResultInterface>({
                    data: criteriaFieldValue.valor,
                    getValor: () => criteriaFieldValue.valor,
                    getExibicao: () => ''
                }),
                40
            ),
            new CriteriaFieldModel(
                'valorEconomico',
                'Valor Econômico do Processo',
                [
                    new IsNotNull('tem'),
                    new IsNull('não tem'),
                    new Equal(),
                    new NotEqual(),
                    new LessThan(),
                    new LessThanEqual(),
                    new GreaterThan(),
                    new GreaterThanEqual(),
                    new Between(),
                ],
                (operator: ComparisonOperatorInterface) => {
                    switch (operator.getExpression()) {
                        case (new Between()).getExpression():
                            return from(import(
                                '@cdk/components/criteria/form-fields/cdk-range-valor-monetario-generico/cdk-range-valor-monetario-generico-form.module'
                                ).then(m => m.CdkRangeValorMonetarioGenericoFormModule))
                        default:
                            return from(import(
                                '@cdk/components/criteria/form-fields/cdk-valor-monetario-generico/cdk-valor-monetario-generico-form.module'
                                ).then(m => m.CdkValorMonetarioGenericoFormModule));
                    }
                },
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    switch (criteriaFieldValue.operator.getExpression()) {
                        case (new IsNull()).getExpression():
                        case (new IsNotNull()).getExpression():
                            return of<CriteriaLoadDataResultInterface>({
                                data: criteriaFieldValue.valor,
                                getValor: () => criteriaFieldValue.valor,
                                getExibicao: () => ''
                            });
                        default:
                            return of<CriteriaLoadDataResultInterface>({
                                data: criteriaFieldValue.valor,
                                getValor: () => criteriaFieldValue.valor,
                                getExibicao: () => criteriaFieldValue.valor
                            })
                    }
                },
                50
            ),
            new CriteriaFieldModel(
                'especieProcesso.id',
                'Processo de Trabalho do Processo',
                [
                    new Equal(),
                    new NotEqual(),
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-especie-processo/cdk-especie-processo-form.module'
                    ).then(m => m.CdkEspecieProcessoFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    const especieProcessoPagination = new Pagination();
                    especieProcessoPagination.populate = ['populateAll'];
                    return {
                        especieProcessoPagination: especieProcessoPagination
                    }
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    if (criteriaFieldValue.valor) {
                        return this._especieProcessoService
                            .get(parseInt(criteriaFieldValue.valor), JSON.stringify(['populateAll']))
                            .pipe(map(
                                (obj: EspecieProcesso) => {
                                    return {
                                        data: obj,
                                        getValor: () => obj.id,
                                        getExibicao: () => obj.nome
                                    }
                                }
                            ))
                    }
                    return of<CriteriaLoadDataResultInterface>(emptyCriteriaLoadDataResult());
                },
                60
            ),
            new CriteriaFieldModel(
                'classificacao.id',
                'Classificação do Processo',
                [
                    new Equal(),
                    new NotEqual(),
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-classificacao/cdk-classificacao-form.module'
                    ).then(m => m.CdkClassificacaoFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    if (criteriaFieldValue.valor) {
                        return this._classificacaoService
                            .get(parseInt(criteriaFieldValue.valor), JSON.stringify(['populateAll']))
                            .pipe(map(
                                (obj: Classificacao) => {
                                    return {
                                        data: obj,
                                        getValor: () => obj.id,
                                        getExibicao: () => obj.nome
                                    }
                                }
                            ))
                    }
                    return of<CriteriaLoadDataResultInterface>(emptyCriteriaLoadDataResult());
                },
                70
            ),
            new CriteriaFieldModel(
                'modalidadeMeio.id',
                'Meio do Processo',
                [
                    new Equal(),
                    new NotEqual(),
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-modalidade-meio/cdk-modalidade-meio-form.module'
                    ).then(m => m.CdkModalidadeMeioFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    if (criteriaFieldValue.valor) {
                        return this._modalidadeMeioService
                            .get(parseInt(criteriaFieldValue.valor), JSON.stringify(['populateAll']))
                            .pipe(map(
                                (obj: ModalidadeMeio) => {
                                    return {
                                        data: obj,
                                        getValor: () => obj.id,
                                        getExibicao: () => obj.valor
                                    }
                                }
                            ))
                    }
                    return of<CriteriaLoadDataResultInterface>(emptyCriteriaLoadDataResult());
                },
                80
            ),
            new CriteriaFieldModel(
                'titulo',
                'Título do Processo',
                [
                    new Contains(),
                    new NotContains(),
                    new StartsWith(),
                    new NotStartsWith(),
                    new EndsWith(),
                    new NotEndsWith(),
                    new Equal(),
                    new NotEqual()
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-texto-generico/cdk-texto-generico-form.module'
                    ).then(m => m.CdkTextoGenericoFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => of<CriteriaLoadDataResultInterface>({
                    data: criteriaFieldValue.valor,
                    getValor: () => criteriaFieldValue.valor,
                    getExibicao: () => criteriaFieldValue.valor
                }),
                90
            ),
            new CriteriaFieldModel(
                'descricao',
                'Descrição do Processo',
                [
                    new Contains(),
                    new NotContains(),
                    new StartsWith(),
                    new NotStartsWith(),
                    new EndsWith(),
                    new NotEndsWith(),
                    new Equal(),
                    new NotEqual()
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-texto-generico/cdk-texto-generico-form.module'
                    ).then(m => m.CdkTextoGenericoFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => of<CriteriaLoadDataResultInterface>({
                    data: criteriaFieldValue.valor,
                    getValor: () => criteriaFieldValue.valor,
                    getExibicao: () => criteriaFieldValue.valor
                }),
                100
            ),
            new CriteriaFieldModel(
                'setorAtual.unidade.modalidadeOrgaoCentral.id',
                'Órgão Central Atual do Processo',
                [
                    new Equal(),
                    new NotEqual(),
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-modalidade-orgao-central/cdk-modalidade-orgao-central-form.module'
                    ).then(m => m.CdkModalidadeOrgaoCentralFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    if (criteriaFieldValue.valor) {
                        return this._modalidadeOrgaoCentralService
                            .get(parseInt(criteriaFieldValue.valor), JSON.stringify(['populateAll']))
                            .pipe(map(
                                (obj: ModalidadeOrgaoCentral) => {
                                    return {
                                        data: obj,
                                        getValor: () => obj.id,
                                        getExibicao: () => obj.valor
                                    }
                                }
                            ))
                    }
                    return of<CriteriaLoadDataResultInterface>(emptyCriteriaLoadDataResult());
                },
                110
            ),
            new CriteriaFieldModel(
                'setorAtual.unidade.id',
                'Unidade Atual do Processo',
                [
                    new Equal(),
                    new NotEqual(),
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-unidade/cdk-unidade-form.module'
                    ).then(m => m.CdkUnidadeFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    if (criteriaFieldValue.valor) {
                        return this._setorService
                            .get(parseInt(criteriaFieldValue.valor), JSON.stringify(['populateAll']))
                            .pipe(map(
                                (obj: Setor) => {
                                    return {
                                        data: obj,
                                        getValor: () => obj.id,
                                        getExibicao: () => obj.nome
                                    }
                                }
                            ))
                    }
                    return of<CriteriaLoadDataResultInterface>(emptyCriteriaLoadDataResult());
                },
                120
            ),
            new CriteriaFieldModel(
                'setorAtual.id',
                'Setor Atual do Processo',
                [
                    new Equal(),
                    new NotEqual()
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-setor/cdk-setor-form.module'
                    ).then(m => m.CdkSetorFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    if (criteriaFieldValue.valor) {
                        return this._setorService
                            .get(parseInt(criteriaFieldValue.valor), JSON.stringify(['populateAll']))
                            .pipe(map(
                                (obj: Setor) => {
                                    return {
                                        data: obj,
                                        getValor: () => obj.id,
                                        getExibicao: () => `${obj.nome} (${obj.unidade.sigla})`
                                    }
                                }
                            ))
                    }
                    return of<CriteriaLoadDataResultInterface>(emptyCriteriaLoadDataResult());
                },
                130
            ),
            new CriteriaFieldModel(
                'localizador.id',
                'Localizador do Processo',
                [
                    new Equal(),
                    new NotEqual()
                ],
                (operator: ComparisonOperatorInterface) => from(import(
                    '@cdk/components/criteria/form-fields/cdk-localizador/cdk-localizador-form.module'
                    ).then(m => m.CdkLocalizadorFormModule)),
                (operator?: ComparisonOperatorInterface) => {
                    return {}
                },
                (criteriaFieldValue: CriteriaFieldValueInterface) => {
                    if (criteriaFieldValue.valor) {
                        return this._localizadorService
                            .get(parseInt(criteriaFieldValue.valor), JSON.stringify(['populateAll']))
                            .pipe(map(
                                (obj: Localizador) => {
                                    return {
                                        data: obj,
                                        getValor: () => obj.id,
                                        getExibicao: () => obj.nome
                                    }
                                }
                            ))
                    }
                    return of<CriteriaLoadDataResultInterface>(emptyCriteriaLoadDataResult());
                },
                140
            ),
        ];
    }
}
